import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adventures } from '../data/adventuresData';
import { useTestimonials } from '../hooks/useTestimonials';

const HomePage = () => {
  const { testimonials, loading: testimonialsLoading } = useTestimonials();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  // Featured adventures for homepage (top 3)
  const featuredAdventures = adventures.slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section 
        id="hero"
        className="relative h-screen bg-gradient-to-b from-ocean-900 via-ocean-800 to-ocean-900 overflow-hidden"
      >
        {/* Background Video/Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1583212292454-1fe6229603b7"
            alt="Underwater meditation scene"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ocean-900/70 via-ocean-800/50 to-ocean-900/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Reconnect With Nature
                <br />
                <span className="text-coral-400">Above & Below</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-10 sm:mb-12 max-w-4xl mx-auto leading-relaxed px-2">
                Join Ahmed Elsayed for transformative diving and camping adventures. 
                Discover mindful connections with Australia's incredible marine life and pristine coastlines.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
                <Link
                  to="/adventures"
                  className="btn-elegant px-8 py-4 text-base sm:text-lg shadow-xl floating-animation"
                  style={{ animationDelay: '0.5s' }}
                >
                  Explore Adventures
                </Link>
                <Link
                  to="/book"
                  className="bg-white/20 backdrop-blur-md hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full text-base sm:text-lg font-semibold transition-all transform hover:scale-105 floating-animation"
                  style={{ animationDelay: '0.7s' }}
                >
                  Book Now
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-white/70 text-center"
          >
            <div className="text-sm mb-2">Scroll to discover</div>
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/70 rounded-full mt-2"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mini About Ahmed */}
      <section id="mini-about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="floating-animation"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_divecamp-payments/artifacts/4rirmim5_Abyss%20Scuba%20Diving%20-%202025-04-12-02-54-00-000-ce28b.png"
                alt="Ahmed Elsayed - MSDT Instructor"
                className="w-full h-80 sm:h-96 object-cover rounded-3xl elegant-shadow-lg"
                style={{ objectPosition: 'center 20%' }}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-slate-800 mb-6">
                Meet Ahmed Elsayed
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                MSDT certified dive instructor with 8+ years of experience creating transformative 
                nature connections. Ahmed combines his mechanical engineering precision with a deep 
                passion for ocean conservation and mindful adventure.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-coral-600">8+</div>
                  <div className="text-slate-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-coral-600">MSDT</div>
                  <div className="text-slate-600">Certified</div>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center text-coral-600 hover:text-coral-700 font-semibold"
              >
                Learn More About Ahmed →
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Adventures */}
      <section id="adventures" className="py-20" style={{ backgroundColor: '#0b4c71' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Featured Adventures</h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Discover our most popular experiences combining mindful exploration with professional guidance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {featuredAdventures.map((adventure, index) => (
              <motion.div
                key={adventure.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={adventure.image}
                    alt={adventure.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-coral-500 text-white px-3 py-1 rounded-full text-sm">
                      {adventure.duration}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{adventure.title}</h3>
                  <p className="text-slate-600 mb-4 line-clamp-2">{adventure.shortDescription}</p>
                  
                  <div className="flex space-x-3">
                    <Link
                      to={`/adventures/${adventure.slug}`}
                      className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 py-3 rounded-xl font-semibold text-center transition-all"
                    >
                      Learn More
                    </Link>
                    <Link
                      to="/book"
                      className="flex-1 bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white py-3 rounded-xl font-semibold text-center transition-all transform hover:scale-105"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/adventures"
              className="bg-gradient-to-r from-ocean-600 to-ocean-700 hover:from-ocean-700 hover:to-ocean-800 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
            >
              View All Adventures
            </Link>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section id="differences" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">What Makes Us Different</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Experience the unique approach that sets Dive Camp Adventures apart
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              {
                icon: "🎒",
                title: "All Gear Included",
                description: "Complete equipment provided - just bring yourself and enthusiasm"
              },
              {
                icon: "🌱",
                title: "Beginner Friendly",
                description: "No experience needed - we'll guide you every step of the way"
              },
              {
                icon: "🏆",
                title: "PADI Certified Guide",
                description: "MSDT certified with 8+ years of professional experience"
              },
              {
                icon: "🧘",
                title: "Mindful Connection",
                description: "Spiritual nature experiences focused on deeper connections"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-elegant text-center p-4 sm:p-6 floating-animation"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">{feature.icon}</div>
                <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1 sm:mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Typical Weekend Preview */}
      <section id="weekend-preview" className="py-20 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">A Typical Weekend Experience</h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Our signature weekend combines the best of land and sea. From sunrise meditation 
                to guided dives, campfire gatherings to stargazing - every moment is designed 
                for meaningful connection with nature and fellow adventurers.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <span className="w-8 h-8 bg-coral-500 rounded-full flex items-center justify-center text-sm font-bold mr-4">1</span>
                  <span>Arrival & mindful camp setup</span>
                </div>
                <div className="flex items-center">
                  <span className="w-8 h-8 bg-coral-500 rounded-full flex items-center justify-center text-sm font-bold mr-4">2</span>
                  <span>Guided diving adventures</span>
                </div>
                <div className="flex items-center">
                  <span className="w-8 h-8 bg-coral-500 rounded-full flex items-center justify-center text-sm font-bold mr-4">3</span>
                  <span>Evening campfire & reflection</span>
                </div>
              </div>
              <Link
                to="/weekend"
                className="btn-elegant px-8 py-4"
              >
                Explore Weekend Trips
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e"
                alt="Campfire under the stars"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-3xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customer Highlights */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">What Our Adventurers Say</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real stories from real people who've experienced transformative adventures
            </p>
          </div>

          <div className="relative">
            {testimonialsLoading ? (
              <div className="max-w-4xl mx-auto text-center">
                <div className="glass-card-blue p-8 rounded-3xl">
                  <div className="animate-pulse">
                    <div className="flex justify-center mb-6">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-3xl text-yellow-400/50">⭐</span>
                      ))}
                    </div>
                    <div className="h-20 bg-white/20 rounded mb-6"></div>
                    <div className="flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 rounded-full mr-4"></div>
                      <div className="text-left">
                        <div className="h-4 bg-white/20 rounded mb-2 w-32"></div>
                        <div className="h-3 bg-white/20 rounded mb-2 w-24"></div>
                        <div className="h-3 bg-white/20 rounded w-40"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : testimonials.length > 0 ? (
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-4xl mx-auto text-center"
              >
                <div className="glass-card-blue p-8 rounded-3xl">
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <span key={i} className="text-3xl text-yellow-400">⭐</span>
                    ))}
                  </div>
                  <blockquote className="text-xl text-white testimonial-text mb-6 leading-relaxed">
                    "{testimonials[currentTestimonial].text}"
                  </blockquote>
                  <div className="flex items-center justify-center">
                    <img
                      src={testimonials[currentTestimonial].image}
                      alt={testimonials[currentTestimonial].name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div className="text-left">
                      <div className="font-semibold text-white testimonial-name">
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className="text-white/80 testimonial-location">
                        {testimonials[currentTestimonial].location}
                      </div>
                      <div className="text-sm text-coral-300 testimonial-adventure">
                        {testimonials[currentTestimonial].adventure}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="max-w-4xl mx-auto text-center">
                <div className="glass-card-blue p-8 rounded-3xl">
                  <div className="text-6xl mb-4">💬</div>
                  <p className="text-xl text-white mb-4">No testimonials available yet</p>
                  <p className="text-white/80">Be the first to share your adventure experience!</p>
                </div>
              </div>
            )}

            {/* Navigation dots */}
            {testimonials.length > 1 && (
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentTestimonial ? 'bg-coral-500' : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/reviews"
              className="text-coral-600 hover:text-coral-700 font-semibold text-lg"
            >
              Read More Reviews →
            </Link>
          </div>
        </div>
      </section>

      {/* Social Media Strip */}
      <section id="social" className="py-16 bg-gradient-to-r from-coral-500 to-coral-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Follow Our Adventures</h2>
          <p className="text-coral-100 mb-8 text-lg">
            Stay connected and see real-time updates from our diving and camping expeditions
          </p>
          <div className="flex justify-center space-x-6">
            {[
              { platform: 'Instagram', url: 'https://instagram.com/divecampadventures', icon: '📷' },
              { platform: 'TikTok', url: 'https://tiktok.com/@divecampadventures', icon: '🎵' },
              { platform: 'YouTube', url: 'https://youtube.com/@divecampadventures', icon: '📺' }
            ].map((social) => (
              <a
                key={social.platform}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 flex items-center"
              >
                <span className="text-2xl mr-2">{social.icon}</span>
                {social.platform}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section id="cta" className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-6">Your Next Adventure Awaits</h2>
          <p className="text-xl text-slate-300 mb-12 max-w-3xl mx-auto">
            Ready to reconnect with nature and discover the magic beneath the waves? 
            Book your transformative adventure today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/adventures"
              className="bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-xl"
            >
              View All Adventures
            </Link>
            <Link
              to="/weekend"
              className="bg-white/20 backdrop-blur-md hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105"
            >
              Book a Dive Weekend
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;